import { T } from '@/ui/Text/Text';
import React, { useMemo } from 'react';
import { Flex } from '@/ui/Flex/Flex';

import { useIntl } from 'react-intl';
import {
  IconChannelSelective,
  IconLanguage,
  IconTrihow,
  IconTrihowBig,
} from '@/icons';
import HorizontalDivider from '@/ui/HorizontalDivider';
import CustomSelect, { MultiSelect } from '@/ui/Select/Select';
import { MagicModel } from '@/types/MagicModel';
import { useNodes } from '@xyflow/react';
import { SensorTrihowSpecificNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType, LanguageOptions } from '../types';
import {
  generateSpecificTrihowChannelOptions,
  generateSpecificTrihowOptions,
} from '../../util/generateSettingsOptions';
import { findLanguageAnyNodes } from '../../util/findLanguageAnyNodes';

function TrihowSpecificSettings({
  settings,
  selectedNode,
  updateNodeData,
  magicModels,
  selectedTrihows,
  setSelectedSensors,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: SensorTrihowSpecificNodeType;
  updateNodeData: (id: string, newData: any) => void;
  magicModels: MagicModel[];
  selectedTrihows: string[];
  setSelectedSensors: React.Dispatch<
    React.SetStateAction<{
      buttons: string[];
      buzzers: string[];
      trihows: string[];
    }>
  >;
  disabled?: boolean;
}) {
  // get all trihows that have "Any" as language to filter the options accordingly
  const nodes = useNodes();
  const { languageAnyTrihows } = findLanguageAnyNodes(nodes);

  const trihowOptions = useMemo(
    () =>
      generateSpecificTrihowOptions(
        magicModels,
        selectedTrihows,
        selectedNode.data.options.trihow,
        settings.languageSelective,
        languageAnyTrihows,
      ),
    [
      magicModels,
      selectedTrihows,
      selectedNode.data.options.trihow,
      settings.languageSelective,
    ],
  );

  const channelOptions = useMemo(
    () => generateSpecificTrihowChannelOptions(settings.nfcReaders),
    [settings.nfcReaders],
  );
  const intl = useIntl();

  const faqs = [
    {
      question: intl.formatMessage({
        defaultMessage:
          'What is the purpose of Channel and Language in this block?',
        id: 'zacMFq',
      }),
      answer: intl.formatMessage({
        defaultMessage:
          'Channel defines on which channel the trihow and language defines under which language the logic for this specific trihow should work. If both are set to "Any", the logic for this specific trihow will work on any channel and language.',
        id: '9DGoms',
      }),
    },
  ];
  const relevantData = selectedNode.data.options;
  return (
    <StyledSettings data-testid="sensor-nfcSpecific-settings">
      <Flex align="center" gap={8}>
        <IconTrihowBig />
        <T h3>
          {intl.formatMessage({
            defaultMessage: 'Specific Trihow',
            id: '+X6I9p',
          })}
        </T>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={16}>
          <MultiSelect
            disabled={disabled ?? false}
            required
            title="Trihow(s)"
            placeholder={intl.formatMessage({
              defaultMessage: 'Select Trihow(s)',
              id: 'OeVISS',
            })}
            icon={<IconTrihow />}
            options={trihowOptions}
            size="small"
            onChange={(e) => {
              updateNodeData(selectedNode.id, {
                ...selectedNode.data,
                options: {
                  ...relevantData,
                  trihow: e
                    ? e.map((item) => ({
                        triggerId: item.value,
                        name: item.label,
                      }))
                    : null,
                },
              });
              setSelectedSensors((prev) => {
                const prevNodeTrihows = Array.isArray(relevantData.trihow)
                  ? relevantData.trihow.map((t) => t.triggerId)
                  : [];
                const newNodeTrihows = Array.isArray(e)
                  ? e.map((entry) => entry.value)
                  : [];
                // Remove trihows previously selected by this node
                const withoutPrevNodeTrihows = prev.trihows.filter(
                  (trihow) => !prevNodeTrihows.includes(trihow),
                );
                // Add new trihows selected by this node
                const updatedTrihows = [
                  ...withoutPrevNodeTrihows,
                  ...newNodeTrihows,
                ];
                // Remove duplicates
                const uniqueTrihows = Array.from(new Set(updatedTrihows));
                return {
                  ...prev,
                  trihows: uniqueTrihows,
                };
              });
            }}
            value={
              relevantData.trihow && relevantData.trihow.length > 0
                ? relevantData.trihow.map((t) => ({
                    label: t.name,
                    value: t.triggerId,
                  }))
                : null
            }
          />
          <HorizontalDivider />
          <Flex direction="column" gap={8}>
            <Flex justify="space-between" align="center">
              <T body1>Channel</T>
              <Flex gap={4}>
                <CustomSelect
                  disabled={disabled ?? false}
                  placeholder="Channel"
                  icon={<IconChannelSelective />}
                  isSearchable={false}
                  options={[
                    ...channelOptions,
                    {
                      label: intl.formatMessage({
                        defaultMessage: 'Any',
                        id: 'ToO3/h',
                      }),
                      value: null,
                    },
                  ]}
                  size="small"
                  onChange={(e) =>
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: {
                        ...relevantData,
                        channel: e?.value || null,
                      },
                    })
                  }
                  value={
                    relevantData.channel
                      ? {
                          label: relevantData.channel,
                          value: relevantData.channel,
                        }
                      : {
                          label: intl.formatMessage({
                            defaultMessage: 'Any',
                            id: 'ToO3/h',
                          }),
                          value: null,
                        }
                  }
                />
              </Flex>
            </Flex>
            {settings.languageSelective && (
              <Flex justify="space-between" align="center">
                <T body1>
                  {intl.formatMessage({
                    defaultMessage: 'Works in Language',
                    id: 'M+Dbq2',
                  })}
                </T>
                <CustomSelect
                  disabled={disabled ?? false}
                  size="small"
                  options={LanguageOptions.map((lang) => ({
                    label: lang.value
                      ? lang.value
                      : intl.formatMessage({
                          defaultMessage: 'Any',
                          id: 'ToO3/h',
                        }),
                    value: lang.value,
                  }))}
                  value={
                    relevantData.language
                      ? {
                          label: relevantData.language,
                          value: relevantData.language,
                        }
                      : {
                          label: intl.formatMessage({
                            defaultMessage: 'Any',
                            id: 'ToO3/h',
                          }),
                          value: null,
                        }
                  }
                  onChange={(e) => {
                    updateNodeData(selectedNode.id, {
                      ...selectedNode.data,
                      options: { ...relevantData, language: e?.value },
                    });
                  }}
                  placeholder="Language"
                  isSearchable={false}
                  icon={<IconLanguage />}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>

      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}

export default TrihowSpecificSettings;
